import { useConfig } from '@/hooks/useConfig/useConfig';
import { usePresentation } from '@/hooks/usePresentation/usePresentation';
import { useThemeAssets } from '@/hooks/useThemeAssets';
import { useTransaction } from '@/hooks/useTransaction/useTransaction';
import { cn } from '@/utils/cn';
import { getCardText } from '@/utils/getCardText';
import phoneIcon from '@assets/tapPhone.svg';
import { Container, FlexContainer } from '@cof/omni-react';
import styled from '@emotion/styled';

const InstructionScreenContainer = styled(FlexContainer)`
  background-color: rgba(242, 242, 243, 1);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  align-items: center;
  justify-content: top;
`;

const InstructionScreenTitle = styled('h3')<{ blockPaddingPx: number }>`
  font-size: 20px;
  font-weight: 600;
  display: flex;
  justify-content: center;
  margin: ${({ blockPaddingPx }) => blockPaddingPx / 2}px;
  padding-left: ${({ blockPaddingPx }) => blockPaddingPx / 2}px;
`;

const InstructionScreenHeader = styled('header')<{
  edgePaddingPx: number;
  blockPaddingPx: number;
}>`
  width: 100%;
  background-color: rgba(1, 61, 91, 1);
  color: white;
  padding: ${({ blockPaddingPx }) => blockPaddingPx * 1.5}px
    ${({ edgePaddingPx }) => edgePaddingPx}px
    ${({ blockPaddingPx }) => blockPaddingPx / 2}px;
  margin-bottom: ${({ blockPaddingPx }) => blockPaddingPx}px;
  h1 {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
`;

const InstructionScreenBody = styled('div')`
  font-weight: 400;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const instructionText = {
  'en-US': {
    headerTitle: 'Ready to read',
    body: [
      "Hold your card flat against the back of your phone, with the card's magnetic stripe facing the phone.",
      'If nothing happens, try moving your card to a different position.',
    ],
  },
  'es-US': {
    headerTitle: 'Listo para leer',
    body: [
      'Sostenga su tarjeta plana contra la parte posterior de su teléfono, con la banda magnética de la tarjeta frente al teléfono.',
      'Si no ocurre nada, intente mover su tarjeta a una posición diferente.',
    ],
  },
};

export const InstructionScreen = () => {
  const { locale } = useConfig();
  const { publicData } = useTransaction();
  const { airkeyTag } = useThemeAssets();
  const { tokens } = usePresentation();
  const cards = publicData?.cardLast4Array || [];

  return (
    <InstructionScreenContainer
      data-testid="InstructionScreen"
      className={cn('bg-page')}
    >
      <InstructionScreenHeader
        data-testid="InstructionScreen--Header"
        edgePaddingPx={tokens.edgePaddingPx}
        blockPaddingPx={tokens.blockPaddingPx}
      >
        <h1>{instructionText[locale].headerTitle}</h1>
      </InstructionScreenHeader>
      <img src={phoneIcon} width="166" alt="icon" role="presentation" />
      <InstructionScreenTitle
        className={cn('text-neutral-primary')}
        blockPaddingPx={tokens.blockPaddingPx}
      >
        {getCardText(cards, locale)}
      </InstructionScreenTitle>
      <InstructionScreenBody className={cn('text-neutral-primary')}>
        <ul>
          <li>{instructionText[locale].body[0]}</li>
          <li>{instructionText[locale].body[1]}</li>
        </ul>
        <Container padding={tokens.screenPadding}>
          <img src={airkeyTag} width="144" alt="tag" role="presentation" />
        </Container>
      </InstructionScreenBody>
    </InstructionScreenContainer>
  );
};
